export interface VideoProperties {
    fps: number;
    width: number;
    height: number;
}

/**
 * Picks the video stream line out of ffmpeg's `-i` banner, e.g.
 *   Stream #0:0[0x1](und): Video: h264 (High) (avc1 / 0x31637661), ...
 */
const VIDEO_STREAM_LINE_RE = /(^|\W)Video:/i;

/**
 * Matches the `WIDTHxHEIGHT` token inside a video stream line.
 *
 * The token is always delimited: preceded by start-of-scan, whitespace or a
 * comma, and followed by whitespace, a comma or the `[SAR ...]` bracket.
 * Both guards are load-bearing, because the line also carries a codec tag
 * (`avc1 / 0x31637661`, `hev1 / 0x31766568`) that a looser pattern reads as
 * dimensions: requiring at least two digits to the *left* of the `x` rules the
 * tag out, since a hex literal only ever has a single `0` in front of its `x`.
 * That misread is what this pattern replaces — it yielded width 0 / height
 * 31637661, and a zero width sent coverTarget() into its fallback, so every
 * source was upscaled and cover-cropped to the full 1080x1920 target.
 */
const VIDEO_DIMENSIONS_RE = /(?:^|[\s,])(\d{2,5})x(\d{2,5})(?=$|[\s,[])/;

/**
 * Reads fps and source dimensions out of the raw stderr banner ffmpeg prints
 * for `-i <file>`. Pure, so it can be exercised without an ffmpeg instance.
 * Anything it cannot find stays 0, which callers treat as "probe failed".
 */
export const parseVideoProperties = (rawInfo: string): VideoProperties => {
  const result: VideoProperties = {
    fps: 0,
    width: 0,
    height: 0,
  };
  if (!rawInfo) {
    return result;
  }

  const fpsMatch = /(\d+)\sfps/i.exec(rawInfo);
  if (fpsMatch?.[1]) {
    result.fps = parseInt(fpsMatch[1], 10);
  }

  // Scan only the video stream line, and only the part after `Video:` — the
  // stream header itself carries an id in hex (`Stream #0:0[0x1]`).
  const videoLine = rawInfo
    .split('\n')
    .find((line) => VIDEO_STREAM_LINE_RE.test(line));
  if (videoLine) {
    const afterVideoTag = videoLine.slice(videoLine.search(/Video:/i) + 'Video:'.length);
    const sizeMatch = VIDEO_DIMENSIONS_RE.exec(afterVideoTag);
    if (sizeMatch?.[1] && sizeMatch[2]) {
      result.width = parseInt(sizeMatch[1], 10);
      result.height = parseInt(sizeMatch[2], 10);
    }
  }

  return result;
};
